// src/hooks/useAdminAuth.js
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // ★ 추가
import { adminLogin, adminMe } from "../api/admin";
import { setAdminAuthErrorHandler } from "../api/adminClient";

const AUTH_KEY = "adm_auth";
const AdminAuthCtx = createContext(null);

export function AdminAuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [booting, setBooting] = useState(true);
    const nav = useNavigate();            // ★ 추가
    const location = useLocation();       // ★ 추가

    // 공통: 로그인 페이지로 보내기
    const goLogin = () => {
        try {
            localStorage.removeItem("adm_token");
            localStorage.removeItem(AUTH_KEY);
        } catch { }
        setUser(null);
        // 이미 /admin/login이면 또 이동하지 않음
        if (!location.pathname.startsWith("/admin/login")) {
            nav("/admin/login", { replace: true });
        }
    };

    // 401/403 시 자동 로그아웃(+이동)
    useEffect(() => {
        // ⚠️ 단일 콜백으로 등록 (이중 화살표 아님)
        setAdminAuthErrorHandler(() => {
            goLogin();
        });
        return () => setAdminAuthErrorHandler(null);
        // nav/location은 Router 컨텍스트라 mount 시 1회만 등록하는 게 안전
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 초기 복원
    useEffect(() => {
        (async () => {
            try {
                const token = localStorage.getItem("adm_token");
                if (!token) {
                    setBooting(false);
                    goLogin();
                    return;
                }

                const cached = localStorage.getItem(AUTH_KEY);
                if (cached) {
                    setUser(JSON.parse(cached));
                } else {
                    const me = await adminMe(); // ← 여기서 throw 가능
                    const role = me.is_super ? "super" : "admin";
                    const next = { id: me.username, role, profile: me };
                    setUser(next);
                    localStorage.setItem(AUTH_KEY, JSON.stringify(next));
                }
            } catch (e) {
                // 토큰 만료(401/403)는 이미 goLogin()이 처리하므로 오버레이만 막고 조용히 무시
                if (e?.status === 401 || e?.status === 403) {
                    // no-op
                } else {
                    // 다른 에러는 콘솔만 찍고(원하면 토스트)
                    console.error(e);
                }
            } finally {
                setBooting(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 세션 동기화
    useEffect(() => {
        try {
            if (user) localStorage.setItem(AUTH_KEY, JSON.stringify(user));
            else localStorage.removeItem(AUTH_KEY);
        } catch { }
    }, [user]);

    // ★ 부팅 이후에라도 토큰이 사라진 경우(수동 삭제 등) 즉시 로그인으로
    useEffect(() => {
        if (!booting && !localStorage.getItem("adm_token")) {
            if (user) setUser(null);
            if (!location.pathname.startsWith("/admin/login")) {
                nav("/admin/login", { replace: true });
            }
        }
    }, [booting, location.pathname, nav, user]);

    // 실제 로그인
    const login = async ({ id, password }) => {
        const { access_token } = await adminLogin({ username: id, password });
        localStorage.setItem("adm_token", access_token);

        const me = await adminMe();
        const role = me.is_super ? "super" : "admin";
        const next = { id: me.username, role, profile: me };
        setUser(next);
        return { ok: true, role };
    };

    const logout = () => {
        goLogin();
    };

    const hasRole = (roles) => (user ? roles.includes(user.role) : false);

    const value = useMemo(
        () => ({ user, isAuthenticated: !!user, booting, login, logout, hasRole }),
        [user, booting]
    );

    return <AdminAuthCtx.Provider value={value}>{children}</AdminAuthCtx.Provider>;
}

export const useAdminAuth = () => useContext(AdminAuthCtx);
